import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Plus, X, CheckCircle, XCircle, Ban, Plane } from "lucide-react";

const STATUS_STYLES = {
  pending: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  approved: "text-[#00F5D4] bg-[#00F5D4]/10 border-[#00F5D4]/20",
  rejected: "text-rose-400 bg-rose-500/10 border-rose-500/20",
  cancelled: "text-[#7d8590] bg-[#1c2128] border-[#30363d] line-through",
};

const LEAVE_TYPE_LABEL = { annual: "Annual leave", sick: "Sick leave", unpaid: "Unpaid leave", other: "Other" };

const EMPTY_FORM = { leave_type: "annual", start_date: "", end_date: "", notes: "" };

export default function TimeOff() {
  const { user } = useAuth();
  const isManager = user?.role === "admin" || user?.role === "owner";

  const [tab, setTab] = useState("mine"); // "mine" | "team" | "allowances"
  const [myRequests, setMyRequests] = useState([]);
  const [balance, setBalance] = useState(null);
  const [teamPending, setTeamPending] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [allowanceEdits, setAllowanceEdits] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [decisionNote, setDecisionNote] = useState({});

  const load = useCallback(async () => {
    try {
      const [reqRes, balRes] = await Promise.all([
        api.get(`/leave/requests?user_id=${user?.user_id}`),
        api.get("/leave/balance"),
      ]);
      setMyRequests(reqRes.data || []);
      setBalance(balRes.data);
      if (isManager) {
        const [pendingRes, membersRes] = await Promise.all([
          api.get("/leave/requests?status=pending"),
          api.get("/team/members"),
        ]);
        setTeamPending((pendingRes.data || []).filter(r => r.user_id !== user?.user_id));
        setTeamMembers(membersRes.data || []);
      }
    } catch {
      toast.error("Failed to load time off data");
    }
  }, [isManager, user?.user_id]);

  useEffect(() => { load(); }, [load]);

  const submitRequest = async () => {
    if (!form.start_date || !form.end_date) return toast.error("Start and end date are required");
    setSaving(true);
    try {
      const { data } = await api.post("/leave/requests", form);
      toast.success(`Requested ${data.days} day(s) of ${LEAVE_TYPE_LABEL[data.leave_type].toLowerCase()}`);
      setShowModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit request");
    } finally {
      setSaving(false);
    }
  };

  const cancelRequest = async (req) => {
    if (!window.confirm(`Cancel this ${LEAVE_TYPE_LABEL[req.leave_type].toLowerCase()} request?`)) return;
    try {
      await api.post(`/leave/requests/${req.request_id}/cancel`);
      toast.success("Request cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to cancel request");
    }
  };

  const decide = async (req, action) => {
    try {
      await api.post(`/leave/requests/${req.request_id}/decision`, { action, note: decisionNote[req.request_id] || "" });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const saveAllowance = async (member) => {
    const edits = allowanceEdits[member.user_id] || {};
    const year = new Date().getFullYear();
    try {
      await api.put(`/leave/allowances/${member.user_id}`, {
        year,
        annual_days: parseFloat(edits.annual_days) || 0,
        sick_days: parseFloat(edits.sick_days) || 0,
      });
      toast.success(`Allowance updated for ${member.name || member.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update allowance");
    }
  };

  const BalanceCard = ({ label, data }) => {
    if (!data) return null;
    const pct = data.allocated > 0 ? Math.min(100, (data.used / data.allocated) * 100) : 0;
    const over = data.remaining < 0;
    return (
      <div className="bg-[#0d1117] border border-[#21262d] rounded-lg p-4">
        <div className="text-xs text-[#7d8590] mb-2">{label}</div>
        <div className="flex items-baseline gap-1.5 mb-2">
          <span className="text-xl font-bold text-white">{data.remaining}</span>
          <span className="text-xs text-[#7d8590]">of {data.allocated} days left</span>
        </div>
        <div className="w-full h-1.5 bg-[#1c2128] rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: over ? "#f43f5e" : "#00F5D4" }} />
        </div>
      </div>
    );
  };

  return (
    <DarkPage><div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Plane className="w-5 h-5 text-[#7d8590]" /> Time Off</h1>
          <p className="text-sm text-[#7d8590] mt-1">Request leave, track your balance, and (for managers) approve the team's requests</p>
        </div>
        <Button onClick={() => { setForm(EMPTY_FORM); setShowModal(true); }} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
          <Plus className="w-4 h-4 mr-1" /> Request time off
        </Button>
      </div>

      {balance && (
        <div className="grid grid-cols-2 gap-4 mb-6 max-w-md">
          <BalanceCard label="Annual leave" data={balance.annual} />
          <BalanceCard label="Sick leave" data={balance.sick} />
        </div>
      )}

      {isManager && (
        <div className="flex bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden w-fit mb-6">
          {[["mine", "My requests"], ["team", `Team approvals${teamPending.length ? ` (${teamPending.length})` : ""}`], ["allowances", "Allowances"]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${tab === key ? "bg-[#1c2128] text-white font-medium" : "text-[#7d8590] hover:text-white"}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {(!isManager || tab === "mine") && (
        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-[#21262d] text-xs text-[#7d8590] font-medium">My requests</div>
          {myRequests.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-[#7d8590]">No time off requested yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#21262d]">
                  {["Type", "Dates", "Days", "Status", ""].map(h => (
                    <th key={h} className="text-left px-4 py-2.5 text-xs text-[#7d8590] font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {myRequests.map(r => (
                  <tr key={r.request_id} className="border-b border-[#21262d] last:border-0">
                    <td className="px-4 py-2.5 text-white">{LEAVE_TYPE_LABEL[r.leave_type]}</td>
                    <td className="px-4 py-2.5 text-[#7d8590] font-mono text-xs">{r.start_date} – {r.end_date}</td>
                    <td className="px-4 py-2.5 text-white">{r.days}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[r.status]}`}>{r.status}</span>
                      {r.status === "rejected" && r.decision_note && <div className="text-[10px] text-rose-400 mt-0.5">{r.decision_note}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {(r.status === "pending" || r.status === "approved") && (
                        <button onClick={() => cancelRequest(r)} className="text-[#484f58] hover:text-rose-400" title="Cancel"><Ban className="w-3.5 h-3.5" /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {isManager && tab === "team" && (
        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-[#21262d] text-xs text-[#7d8590] font-medium">Pending approval</div>
          {teamPending.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-[#7d8590]">No pending requests from the team.</div>
          ) : (
            <div className="divide-y divide-[#21262d]">
              {teamPending.map(r => (
                <div key={r.request_id} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-white text-sm font-medium">{r.user_name}</div>
                    <div className="text-xs text-[#7d8590]">{LEAVE_TYPE_LABEL[r.leave_type]} · {r.start_date} – {r.end_date} · {r.days} day(s)</div>
                    {r.notes && <div className="text-xs text-[#7d8590] mt-0.5 italic">"{r.notes}"</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      value={decisionNote[r.request_id] || ""}
                      onChange={e => setDecisionNote(v => ({ ...v, [r.request_id]: e.target.value }))}
                      placeholder="Note (optional)…"
                      className="h-8 px-2 text-xs bg-[#161b22] border border-[#30363d] text-white rounded-md w-40"
                    />
                    <button onClick={() => decide(r, "approve")} className="text-xs px-3 py-1.5 rounded-full border border-[#00F5D4]/30 text-[#00F5D4] bg-[#00F5D4]/10 hover:bg-[#00F5D4]/20 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Approve
                    </button>
                    <button onClick={() => decide(r, "reject")} className="text-xs px-3 py-1.5 rounded-full border border-rose-500/20 text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 flex items-center gap-1">
                      <XCircle className="w-3 h-3" /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isManager && tab === "allowances" && (
        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-[#21262d] text-xs text-[#7d8590] font-medium">
            Annual allowances for {new Date().getFullYear()} · unset members default to 25 annual / 10 sick days
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#21262d]">
                {["Name", "Annual days", "Sick days", ""].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-xs text-[#7d8590] font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {teamMembers.map(m => (
                <tr key={m.user_id} className="border-b border-[#21262d] last:border-0">
                  <td className="px-4 py-2.5 text-white">{m.name || m.email}</td>
                  <td className="px-4 py-2.5">
                    <input type="number" step="0.5" placeholder="25"
                      value={allowanceEdits[m.user_id]?.annual_days ?? ""}
                      onChange={e => setAllowanceEdits(v => ({ ...v, [m.user_id]: { ...v[m.user_id], annual_days: e.target.value } }))}
                      className="w-20 h-8 px-2 text-xs bg-[#161b22] border border-[#30363d] text-white rounded-md" />
                  </td>
                  <td className="px-4 py-2.5">
                    <input type="number" step="0.5" placeholder="10"
                      value={allowanceEdits[m.user_id]?.sick_days ?? ""}
                      onChange={e => setAllowanceEdits(v => ({ ...v, [m.user_id]: { ...v[m.user_id], sick_days: e.target.value } }))}
                      className="w-20 h-8 px-2 text-xs bg-[#161b22] border border-[#30363d] text-white rounded-md" />
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => saveAllowance(m)} className="text-[11px] text-[#00F5D4] hover:underline">Save</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Request modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">Request time off</h2>
              <button onClick={() => setShowModal(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Type</label>
                <select value={form.leave_type} onChange={e => setForm(f => ({ ...f, leave_type: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                  {Object.entries(LEAVE_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Start date *</label>
                  <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">End date *</label>
                  <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm resize-none" />
              </div>
              <p className="text-[11px] text-[#7d8590]">Weekends are excluded automatically; the day count is calculated when you submit.</p>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={submitRequest} disabled={saving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {saving ? "Submitting…" : "Submit request"}
              </Button>
              <Button onClick={() => setShowModal(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
